// @ flow
import React, {useEffect, useState} from "react";
import {Col, Row, Tabs} from "antd";
import {SearchText} from "../../_defaultComponents/FilterComponents";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";
import {AssignedUserGroupsTab} from "./components/tabs/assignedUserGroupsTab/AssignedUserGroupsTab";
import {UserPermissionPoliciesTab} from "./components/tabs/userPermissionPoliciesTab/UserPermissionPoliciesTab";
import {AllowedPermissionPoliciesTab} from "./components/tabs/allowedPermissionPoliciesTab/AllowedPermissionPoliciesTab";
import {DisallowedPermissionPoliciesTab} from "./components/tabs/disallowedPermissionPoliciesTab/DisallowedPermissionPoliciesTab";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

export const UserAuthorization = ({userId}) => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation(["userAuthorization"]);
    const [activeTab, setActiveTab] = useState("allPermissionPolicies");
    const [searchText, setSearchText] = useState();
    const [currentPage, setCurrentPage] = useState(1);

    const [assignedUserGroupsRecall, setAssignedUserGroupsRecall] = useState(false);
    const [allowedPermissionPoliciesRecall, setAllowedPermissionPoliciesRecall] = useState(false);
    const [disallowedPermissionPoliciesRecall, setDisallowedPermissionPoliciesRecall] = useState(
        false
    );

    const [userPermissionPoliciesLoading, userPermissionPolicies] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.userStore.getUserPermissionPolicies.bind(null, {userId}),
        dependencies: [
            assignedUserGroupsRecall,
            allowedPermissionPoliciesRecall,
            disallowedPermissionPoliciesRecall
        ],
        initialCall: true
    });

    const [assignedUserGroupsLoading, assignedUserGroups] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.userStore.getAssignedUserGroups.bind(null, {userId}),
        dependencies: [assignedUserGroupsRecall],
        initialCall: true
    });

    const [allowedPermissionPoliciesLoading, allowedPermissionPolicies] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.userStore.getAllowedPermissionPolicies.bind(null, {userId}),
        dependencies: [allowedPermissionPoliciesRecall],
        initialCall: true
    });

    const [disallowedPermissionPoliciesLoading, disallowedPermissionPolicies] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.userStore.getDisallowedPermissionPolicies.bind(null, {userId}),
        dependencies: [disallowedPermissionPoliciesRecall],
        initialCall: true
    });

    const onSearch = value => {
        setCurrentPage(1);
        setSearchText(value);
    };

    const setUrl = () => {
        let path = `${history.location.pathname}?`;
        if (activeTab) {
            path += "&tab=" + activeTab;
        }
        if (currentPage) {
            path += "&page=" + currentPage;
        }
        if (searchText) {
            path += "&searchText=" + searchText;
        }

        history.push(path);
    };

    useEffect(() => {
        const params = new URLSearchParams(history.location.search);
        const activeTab = params.get("tab");
        const page = params.get("page");
        const searchText = params.get("searchText");
        if (activeTab) {
            setActiveTab(activeTab);
        }
        if (page) {
            setCurrentPage(page);
        }
        if (searchText) {
            setSearchText(searchText);
        } else {
            setSearchText("");
        }
    }, []);

    useEffect(() => {
        setUrl();
    }, [currentPage, searchText, activeTab]);

    const onChangeTab = key => {
        setActiveTab(key);
        setCurrentPage(1);
    };
    return (
        <Row type="flex" justify="space-between" data-testid="userAuthorizationScreen">
            <Col span={24}>
                <Tabs
                    onChange={onChangeTab}
                    activeKey={activeTab}
                    tabBarExtraContent={
                        typeof searchText === "string" && (
                            <SearchText
                                showLabel={false}
                                showPlaceholder={true}
                                value={searchText}
                                onChange={onSearch}
                            />
                        )
                    }
                >
                    <Tabs.TabPane
                        tab={t("tabs.allPermissionPolicies", {ns: "userAuthorization"})}
                        key="allPermissionPolicies"
                        active={activeTab === "allPermissionPolicies"}
                        data-testid="allPermissionPolicies_tabPane"
                    >
                        <UserPermissionPoliciesTab
                            permissionPolicies={userPermissionPolicies}
                            loading={userPermissionPoliciesLoading}
                            csrSearchText={searchText}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={t("tabs.userGroups", {ns: "userAuthorization"})}
                        key="userGroups"
                        data-testid="userGroups_tabPane"
                    >
                        <AssignedUserGroupsTab
                            userId={userId}
                            userGroups={assignedUserGroups?.rows || []}
                            loading={assignedUserGroupsLoading}
                            csrSearchText={searchText}
                            recall={() => setAssignedUserGroupsRecall(!assignedUserGroupsRecall)}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={t("tabs.allowedPermissionPolicies", {ns: "userAuthorization"})}
                        key="allowedPermissionPolicies"
                        data-testid="allowedPermissionPolicies_tabPane"
                    >
                        <AllowedPermissionPoliciesTab
                            userId={userId}
                            permissionPolicies={allowedPermissionPolicies?.rows || []}
                            csrSearchText={searchText}
                            loading={allowedPermissionPoliciesLoading}
                            recall={() =>
                                setAllowedPermissionPoliciesRecall(!allowedPermissionPoliciesRecall)
                            }
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={t("tabs.disallowedPermissionPolicies", {ns: "userAuthorization"})}
                        key="disallowedPermissionPolicies"
                        data-testid="disallowedPermissionPolicies_tabPane"
                    >
                        <DisallowedPermissionPoliciesTab
                            userId={userId}
                            permissionPolicies={disallowedPermissionPolicies?.rows || []}
                            csrSearchText={searchText}
                            loading={disallowedPermissionPoliciesLoading}
                            recall={() =>
                                setDisallowedPermissionPoliciesRecall(
                                    !disallowedPermissionPoliciesRecall
                                )
                            }
                        />
                    </Tabs.TabPane>
                </Tabs>
            </Col>
        </Row>
    );
};
